import { unwrap } from '@/lib/apiEnvelope'
import { httpClient } from '@/lib/httpClient'
import { UPLOAD_TIMEOUT_MS } from '@/lib/upload'
import type { ApiResponse } from '@/types/api'

import type { ArquivoColaborador } from '../types/employee'
import type {
  AvisoFeriasPayload,
  Ferias,
  FeriasPayload,
  FeriasSaldo,
  FeriasUpdatePayload,
} from '../types/ferias'
import type {
  ArquivoAnexo,
  Atestado,
  AtestadoPayload,
  Dependente,
  DependentePayload,
  EmployeeDocumento,
} from '../types/subResources'

async function getList<T>(path: string): Promise<T[]> {
  const response = await httpClient.get<ApiResponse<T[]>>(path)
  return unwrap(response)
}

const dependentesPath = (colaboradorId: string) => `/Colaborador/${colaboradorId}/dependentes`
const atestadosPath = (colaboradorId: string) => `/Colaborador/${colaboradorId}/atestados`
const feriasPath = (colaboradorId: string) => `/Colaborador/${colaboradorId}/ferias`

export const subResourcesApi = {
  documentos: (colaboradorId: string) =>
    getList<EmployeeDocumento>(`/Colaborador/${colaboradorId}/documentos-arquivo`),
  removeDocumento: async (colaboradorId: string, documentoId: string): Promise<void> => {
    await httpClient.delete(`/Colaborador/${colaboradorId}/documentos-arquivo/${documentoId}`)
  },
  arquivos: (colaboradorId: string) =>
    getList<ArquivoColaborador>(`/Colaborador/${colaboradorId}/arquivos`),
  dependentes: (colaboradorId: string) => getList<Dependente>(dependentesPath(colaboradorId)),
  createDependente: async (
    colaboradorId: string,
    payload: DependentePayload,
  ): Promise<Dependente> => {
    const response = await httpClient.post<ApiResponse<Dependente>>(
      dependentesPath(colaboradorId),
      payload,
      { timeout: UPLOAD_TIMEOUT_MS },
    )
    return unwrap(response)
  },
  updateDependente: async (
    colaboradorId: string,
    dependenteId: string,
    payload: DependentePayload,
  ): Promise<Dependente> => {
    const response = await httpClient.put<ApiResponse<Dependente>>(
      `${dependentesPath(colaboradorId)}/${dependenteId}`,
      payload,
      { timeout: UPLOAD_TIMEOUT_MS },
    )
    return unwrap(response)
  },
  /** A API também apaga os arquivos do dependente. */
  removeDependente: async (colaboradorId: string, dependenteId: string): Promise<void> => {
    await httpClient.delete(`${dependentesPath(colaboradorId)}/${dependenteId}`)
  },
  dependenteArquivos: (colaboradorId: string, dependenteId: string) =>
    getList<ArquivoAnexo>(`${dependentesPath(colaboradorId)}/${dependenteId}/arquivos`),
  atestados: (colaboradorId: string) => getList<Atestado>(atestadosPath(colaboradorId)),
  createAtestado: async (colaboradorId: string, payload: AtestadoPayload): Promise<Atestado> => {
    const response = await httpClient.post<ApiResponse<Atestado>>(
      atestadosPath(colaboradorId),
      payload,
      { timeout: UPLOAD_TIMEOUT_MS },
    )
    return unwrap(response)
  },
  updateAtestado: async (
    colaboradorId: string,
    atestadoId: string,
    payload: AtestadoPayload,
  ): Promise<Atestado> => {
    const response = await httpClient.put<ApiResponse<Atestado>>(
      `${atestadosPath(colaboradorId)}/${atestadoId}`,
      payload,
      { timeout: UPLOAD_TIMEOUT_MS },
    )
    return unwrap(response)
  },
  /** A API também apaga os arquivos do atestado. */
  removeAtestado: async (colaboradorId: string, atestadoId: string): Promise<void> => {
    await httpClient.delete(`${atestadosPath(colaboradorId)}/${atestadoId}`)
  },
  atestadoArquivos: (colaboradorId: string, atestadoId: string) =>
    getList<ArquivoAnexo>(`${atestadosPath(colaboradorId)}/${atestadoId}/arquivos`),
  ferias: (colaboradorId: string) => getList<Ferias>(feriasPath(colaboradorId)),
  /** Um item por período aquisitivo, do mais antigo para o mais recente. */
  feriasSaldo: (colaboradorId: string) =>
    getList<FeriasSaldo>(`${feriasPath(colaboradorId)}/saldo`),
  createFerias: async (colaboradorId: string, payload: FeriasPayload): Promise<Ferias> => {
    const response = await httpClient.post<ApiResponse<Ferias>>(feriasPath(colaboradorId), payload)
    return unwrap(response)
  },
  updateFerias: async (
    colaboradorId: string,
    feriasId: string,
    payload: FeriasUpdatePayload,
  ): Promise<Ferias> => {
    const response = await httpClient.put<ApiResponse<Ferias>>(
      `${feriasPath(colaboradorId)}/${feriasId}`,
      payload,
    )
    return unwrap(response)
  },
  removeFerias: async (colaboradorId: string, feriasId: string): Promise<void> => {
    await httpClient.delete(`${feriasPath(colaboradorId)}/${feriasId}`)
  },
  /** Envia/substitui o documento do aviso e/ou altera a exibição. Devolve a férias atualizada. */
  salvarAvisoFerias: async (
    colaboradorId: string,
    feriasId: string,
    payload: AvisoFeriasPayload,
  ): Promise<Ferias> => {
    const response = await httpClient.put<ApiResponse<Ferias>>(
      `${feriasPath(colaboradorId)}/${feriasId}/aviso`,
      payload,
      { timeout: UPLOAD_TIMEOUT_MS },
    )
    return unwrap(response)
  },
  /** 404 se não havia aviso. */
  removerAvisoFerias: async (colaboradorId: string, feriasId: string): Promise<Ferias> => {
    const response = await httpClient.delete<ApiResponse<Ferias>>(
      `${feriasPath(colaboradorId)}/${feriasId}/aviso`,
    )
    return unwrap(response)
  },
}
